import { ApiService } from '../apiService'
import type { SchedulingMode, SessionScheduling } from '../types/interest'
import { ApiResponse } from '../types/common'
import {
  GroupReadinessDto,
  SessionStateDto,
  SessionListDto,
  JournalEntryDto,
  SessionReferenceDto,
  CreateSessionRequest,
  CreateSessionResponseDto,
  SubmitInputRequest,
  AddJournalNoteRequest,
  AddMapMarkerRequest,
  CastVoteRequest,
  CastVoteResponseDto,
  CastVoteResultDto,
  ProcessResponseDto,
  InputResponseDto,
  VotingProgressDto,
  SessionDebugSummaryDto,
  SessionDebugDetailDto,
  AbandonResponseDto,
  SessionInviteDto,
  InviteFriendsResultDto,
  CreateSessionInviteRequest,
  ShareCodeResponse,
  SessionParticipantInfo,
  InitiateBootVoteRequest,
  CastBootVoteRequest,
  SessionBootVoteDto,
  MarkJournalReadRequest,
  SessionStartValidationDto,
  AdventureSessionFullDto,
  UpdateSessionSettingsRequest,
  DisclosedRoutePoint,
  SessionTransitionResultDto,
  RaceStandingsDto,
} from '../types/session'

// Location update request
export interface UpdateLocationRequest {
  latitude: number
  longitude: number
  accuracy?: number
}

// Presence update request
export interface UpdatePresenceRequest {
  isActive: boolean
}

export const sessionsApi = {
  // ========================
  // Session Management
  // ========================

  // Get all active sessions for current user
  getActiveSessions: () =>
    ApiService.get<SessionListDto[]>('/api/sessions/active'),

  // Get all sessions for current user including completed ones (for history)
  getAllSessions: (params?: { includeCompleted?: boolean }) =>
    ApiService.get<SessionListDto[]>('/api/sessions/active', params),

  // Get active session for a specific adventure (to check if user has existing session)
  getActiveSessionForAdventure: (adventureId: string) =>
    ApiService.get<SessionListDto>(`/api/sessions/active/${adventureId}`),

  // Get session by ID
  getSession: (sessionId: string) =>
    ApiService.get<SessionStateDto>(`/api/sessions/${sessionId}`),

  // Create a new session
  // Returns AdventureSessionFullDto directly (201 Created, not wrapped in ApiResponse)
  createSession: (data: CreateSessionRequest) =>
    ApiService.post<AdventureSessionFullDto>('/api/sessions', data),

  // Start a session (moves from NotStarted to InProgress)
  startSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/start`),

  // Get current session state (for polling/resume)
  getSessionState: (sessionId: string) =>
    ApiService.get<SessionStateDto>(`/api/sessions/${sessionId}/state`),

  // Process current node (auto-advance through reward nodes)
  processSession: (sessionId: string) =>
    ApiService.post<ApiResponse<ProcessResponseDto>>(`/api/sessions/${sessionId}/process`),

  // Submit input for a challenge node
  submitInput: (sessionId: string, data: SubmitInputRequest) =>
    ApiService.post<ApiResponse<InputResponseDto>>(`/api/sessions/${sessionId}/input`, data),

  // Transition to a specific edge (for branching)
  transitionSession: (sessionId: string, data: { edgeId: string }) =>
    ApiService.post<ApiResponse<ProcessResponseDto>>(`/api/sessions/${sessionId}/transition`, data),

  // Pause session
  pauseSession: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/pause`),

  // Resume paused session
  resumeSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/resume`),

  // Abandon session (user wants to quit) — session ends as Abandoned.
  abandonSession: (sessionId: string) =>
    ApiService.post<ApiResponse<AbandonResponseDto>>(`/api/sessions/${sessionId}/abandon`),

  // Player-initiated finish ("Finish Adventure") — session ends as Completed.
  finishSession: (sessionId: string) =>
    ApiService.post<ApiResponse<AbandonResponseDto>>(`/api/sessions/${sessionId}/finish`),

  // Player-initiated give-up ("Give Up") — session ends as Failed.
  giveUpSession: (sessionId: string) =>
    ApiService.post<ApiResponse<AbandonResponseDto>>(`/api/sessions/${sessionId}/give-up`),

  // Upload the locally-recorded GPS track for a ranked session once it ends.
  // The body is the gzip-compressed JSON track; the server stores it for trail
  // audits and prunes it after 90 days.
  uploadSessionTrack: (sessionId: string, track: Blob, pointCount: number, durationMs: number) =>
    ApiService.post<{ message: string }>(
      `/api/sessions/${sessionId}/track?pointCount=${pointCount}&durationMs=${durationMs}`,
      track,
      { headers: { 'Content-Type': 'application/octet-stream' } }
    ),

  // ========================
  // Route Node-Points (Phase 3 master route runtime)
  // ========================

  // Disclose a route node-point's note/photos to the calling user once they
  // physically reach it. Idempotent on the server; returns the per-user content.
  discloseRoutePoint: (sessionId: string, pointId: string): Promise<DisclosedRoutePoint> =>
    ApiService.post<DisclosedRoutePoint>(
      `/api/sessions/${sessionId}/route-points/${pointId}/disclose`
    ),

  // Report a continuous off-track corridor breach so the server fails the
  // session (for routes configured with OffTrack.Action === 'fail'). Returns the
  // updated session state/status so the client can react (terminal redirect).
  offTrackFail: (sessionId: string, reason?: string) =>
    ApiService.post<SessionStateDto>(
      `/api/sessions/${sessionId}/route/off-track-fail`,
      { reason }
    ),

  // Signal that the player has finished traversing the active Route node's
  // section (all its node points reached per the route's completion mode). The
  // Route node waits at AwaitInput until this fires, then the flow advances.
  // Returns the standard transition result (updated state + new journal entries).
  completeRouteTraversal: (sessionId: string, branchKey: string = 'completed') =>
    ApiService.post<SessionTransitionResultDto>(
      `/api/sessions/${sessionId}/route/complete`,
      { branchKey }
    ),

  // ========================
  // Group Session Management
  // ========================

  // Join an existing group session
  joinSession: (sessionId: string) =>
    ApiService.post<ApiResponse<SessionStateDto>>(`/api/sessions/${sessionId}/join`),

  // Leave a group session
  leaveSession: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/leave`),

  // Update user's location in session
  updateLocation: (sessionId: string, data: UpdateLocationRequest) =>
    ApiService.put<ApiResponse<void>>(`/api/sessions/${sessionId}/location`, data),

  // Update user's presence status
  updatePresence: (sessionId: string, data: UpdatePresenceRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/presence`, data),

  // "Notify Again" — re-ping moderators on a session sitting on a
  // ModeratorApproval node. Server returns 429 with cooldownSecondsRemaining
  // when called within the 30s throttle window.
  renotifyModerators: (sessionId: string) =>
    ApiService.post<{
      success: boolean
      notifiedCount: number
      errorMessage: string | null
      cooldownSecondsRemaining: number | null
    }>(`/api/sessions/${sessionId}/moderator-approval/notify-again`),

  // ========================
  // Journal
  // ========================

  // Get journal entries for a session
  getJournal: (sessionId: string, params?: { skip?: number; take?: number }) =>
    ApiService.get<JournalEntryDto[]>(`/api/sessions/${sessionId}/journal`, params),

  // Group race standings. The id may be the race's lobby (parent) or any racer's own run.
  getRaceStandings: async (sessionId: string): Promise<RaceStandingsDto> => {
    const res = await ApiService.get<RaceStandingsDto | { data: RaceStandingsDto }>(`/api/sessions/${sessionId}/standings`)
    return 'entries' in res ? res : res.data
  },

  // Route progress of a racer's own run (0..1). The server keeps one report per 30 s, clamps it
  // and never lets it go backwards; checkpoints and finishes it records by itself.
  reportRaceProgress: (childSessionId: string, fraction: number) =>
    ApiService.post<void>(`/api/sessions/${childSessionId}/race-progress`, { fraction }),

  // The references a session has filed, in pass order (text read from the session's own version)
  getReferences: (sessionId: string) =>
    ApiService.get<SessionReferenceDto[]>(`/api/sessions/${sessionId}/references`),

  // Add a user note to journal
  addJournalNote: (sessionId: string, data: AddJournalNoteRequest) =>
    ApiService.post<ApiResponse<JournalEntryDto>>(`/api/sessions/${sessionId}/journal/note`, data),

  // Add a map marker to journal
  addMapMarker: (sessionId: string, data: AddMapMarkerRequest) =>
    ApiService.post<ApiResponse<JournalEntryDto>>(`/api/sessions/${sessionId}/journal/marker`, data),

  // ========================
  // Voting (Group Sessions)
  // ========================

  // Cast a vote in a voting round
  castVote: (sessionId: string, data: CastVoteRequest) =>
    ApiService.post<ApiResponse<CastVoteResultDto>>(`/api/sessions/${sessionId}/voting/cast`, data),

  // Force-resolve a voting round (admin/moderator)
  forceResolveVoting: (sessionId: string, roundId: string) =>
    ApiService.post<ApiResponse<CastVoteResultDto>>(`/api/sessions/${sessionId}/voting/${roundId}/resolve`),

  // Vote for the group's new host after the host dropped or left (204 once it is settled)
  voteHost: (sessionId: string, candidateUserId: string) =>
    ApiService.post<unknown>(`/api/sessions/${sessionId}/host-election/vote`, { candidateUserId }),

  // Get voting progress for a round
  getVotingProgress: (sessionId: string, roundId: string) =>
    ApiService.get<VotingProgressDto>(`/api/sessions/${sessionId}/voting/${roundId}/progress`),

  // ========================
  // Invites & Sharing
  // ========================

  // Create an invite for a session
  // ----- Agreeing a start time -----

  /** The session's time poll. Participants only. */
  getScheduling: (sessionId: string) =>
    ApiService.get<SessionScheduling>(`/api/sessions/${sessionId}/scheduling`),

  /** Turn the poll on or off, and set its deadline. Owner only. */
  configureScheduling: (sessionId: string, data: { mode: SchedulingMode; votingClosesAt?: string | null }) =>
    ApiService.put<SessionScheduling>(`/api/sessions/${sessionId}/scheduling`, data),

  /** Put a start time forward. Proposing also votes for it — nobody suggests a time they cannot make. */
  proposeTime: (sessionId: string, proposedStartUtc: string) =>
    ApiService.post<SessionScheduling>(`/api/sessions/${sessionId}/scheduling/times`, { proposedStartUtc }),

  /** Withdraw a time. Proposer or owner only. */
  withdrawTime: (sessionId: string, optionId: string) =>
    ApiService.delete<SessionScheduling>(`/api/sessions/${sessionId}/scheduling/times/${optionId}`),

  voteForTime: (sessionId: string, optionId: string) =>
    ApiService.post<SessionScheduling>(`/api/sessions/${sessionId}/scheduling/times/${optionId}/vote`),

  withdrawVoteForTime: (sessionId: string, optionId: string) =>
    ApiService.delete<SessionScheduling>(`/api/sessions/${sessionId}/scheduling/times/${optionId}/vote`),

  /**
   * Close voting now rather than at the deadline. Owner only.
   *
   * Runs exactly the resolution the deadline would have run — same quorum, same tie-break — so
   * closing early cannot produce an outcome the deadline would not have.
   */
  closeScheduling: (sessionId: string) =>
    ApiService.post<SessionScheduling>(`/api/sessions/${sessionId}/scheduling/close`),

  /**
   * Take the place offered on an alternative outing after a time vote split the group.
   *
   * `proposalId`, not a session id — the alternative is not a session until somebody accepts.
   * The first acceptor creates it and owns it; everybody after joins what they created.
   */
  acceptSplit: (proposalId: string) =>
    ApiService.post<{ sessionId: string }>(`/api/sessions/splits/${proposalId}/accept`),

  /** Turn the alternative down. Leaves nothing behind. */
  declineSplit: (proposalId: string) =>
    ApiService.post<void>(`/api/sessions/splits/${proposalId}/decline`),

  createInvite: (sessionId: string, data: CreateSessionInviteRequest) =>
    ApiService.post<ApiResponse<SessionInviteDto>>(`/api/sessions/${sessionId}/invites`, data),

  // Get invites for a session
  getInvites: (sessionId: string) =>
    ApiService.get<SessionInviteDto[]>(`/api/sessions/${sessionId}/invites`),

  /**
   * The host invites several friends at once. Host-only and friends-only on the server; each person
   * comes back with their own outcome, so one bad pick does not sink the rest. 400 when the whole
   * request is refused (not the host, started, full), 429 when the host has sent too many.
   */
  inviteFriends: (sessionId: string, userIds: string[]) =>
    ApiService.post<InviteFriendsResultDto>(`/api/sessions/${sessionId}/invites/friends`, { userIds }),

  /** Before sending: every buddy of the host, `Eligible` or greyed with a short neutral reason. Host only. */
  getInviteEligibility: (sessionId: string) =>
    ApiService.get<InviteFriendsResultDto>(`/api/sessions/${sessionId}/invites/eligibility`),

  /** The host takes back an invite nobody has answered. */
  cancelInvite: (sessionId: string, userId: string) =>
    ApiService.delete<{ message?: string }>(`/api/sessions/${sessionId}/invites/${encodeURIComponent(userId)}`),

  // Accept the caller's invite. The server keys it by session and caller; there is no
  // invite-id route (`/invites/{inviteId}/accept` never existed and 404'd).
  acceptInvite: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/accept`),

  // Decline the caller's invite — same route as declineMyInvite, kept for existing callers.
  declineInvite: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/decline`),

  // Decline an invite for the authenticated user (no invite ID needed)
  declineMyInvite: (sessionId: string) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/invites/decline`),

  // Generate a share code for a session
  generateShareCode: (sessionId: string) =>
    ApiService.post<ShareCodeResponse>(`/api/sessions/${sessionId}/share-code`),

  // Toggle anonymity for a session
  toggleAnonymity: (sessionId: string, isAnonymous: boolean) =>
    ApiService.patch<ApiResponse<void>>(`/api/sessions/${sessionId}/anonymity`, { isAnonymous }),

  // Get participants for a session
  getParticipants: (sessionId: string) =>
    ApiService.get<SessionParticipantInfo[]>(`/api/sessions/${sessionId}/participants`),

  // ========================
  // Ready / Kick / Boot Vote
  // ========================

  // Mark self as ready (or unready) in a group session
  markReady: (sessionId: string, ready: boolean = true) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/ready?ready=${ready}`),

  // Kick a participant (owner only)
  kickParticipant: (sessionId: string, targetUserId: string, reason?: string) =>
    ApiService.post<ApiResponse<void>>(
      `/api/sessions/${sessionId}/kick/${targetUserId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`
    ),

  // Initiate a boot vote against a participant
  initiateBootVote: (sessionId: string, data: InitiateBootVoteRequest) =>
    ApiService.post<ApiResponse<SessionBootVoteDto>>(`/api/sessions/${sessionId}/vote-boot`, data),

  // Cast a vote in an active boot vote
  castBootVote: (sessionId: string, voteId: string, data: CastBootVoteRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/vote-boot/${voteId}/cast`, data),

  // Get active boot votes for a session
  getActiveBootVotes: (sessionId: string) =>
    ApiService.get<SessionBootVoteDto[]>(`/api/sessions/${sessionId}/vote-boot`),

  // Mark journal entries as read up to a sequence number
  markJournalRead: (sessionId: string, data: MarkJournalReadRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/sessions/${sessionId}/journal/mark-read`, data),

  // ========================
  // A planned group
  // ========================

  /** Every member against the full join gate, now (members only). The same report a refused Start carries. */
  getReadiness: (sessionId: string) =>
    ApiService.get<GroupReadinessDto>(`/api/sessions/${sessionId}/readiness`),

  /** Host moves the planned start (`null` = when everyone's ready). Members are told. */
  reschedule: (sessionId: string, startDateTime: string | null) =>
    ApiService.put<{ scheduledStartDate?: string | null }>(`/api/sessions/${sessionId}/schedule`, { startDateTime }),

  /** Host calls off a group that has not started. Members and invitees are told. */
  cancelGroup: (sessionId: string) =>
    ApiService.post<{ message?: string }>(`/api/sessions/${sessionId}/cancel`),

  /**
   * Host ends the group for everyone: a lobby is called off, a run under way ends, a race closes (runs still going
   * did not finish). Any id of the group works, a racer's own run included. Everybody else is told.
   */
  endGroup: (sessionId: string) =>
    ApiService.post<{ message?: string }>(`/api/sessions/${sessionId}/end`),

  // Validate session can be started (checks ready state, proximity, etc.)
  validateSessionStart: (sessionId: string) =>
    ApiService.get<SessionStartValidationDto>(`/api/sessions/${sessionId}/validate-start`),

  // Update session settings (owner only, editable fields)
  updateSessionSettings: (sessionId: string, data: UpdateSessionSettingsRequest) =>
    ApiService.put<{ message: string }>(`/api/sessions/${sessionId}/settings`, data),

  // ========================
  // Debug Endpoints (Maintainers/Owners only)
  // ========================

  // Get all sessions for an adventure (maintainers/owners only)
  getAdventureSessions: (adventureId: string, params?: { includeCompleted?: boolean }) =>
    ApiService.get<SessionDebugSummaryDto[]>(`/api/sessions/adventure/${adventureId}`, params),

  // Get detailed session debug data (maintainers/owners only)
  getSessionDebug: (sessionId: string) =>
    ApiService.get<SessionDebugDetailDto>(`/api/sessions/${sessionId}/debug`),
}
